import React, { useEffect, useMemo, useState } from 'react';
import { signalementsApi, SIG_CATEGORIES, STATUS_LABEL } from '../services/signalementsApi';
import { getToken } from '../admin/auth';
import './Signaler.css';

const EMPTY_FORM = {
  title: '',
  description: '',
  category: 'voirie',
  priority: 'normal',
  location: '',
  anonymous: true,
  reporterName: '',
  reporterEmail: '',
  reporterPhone: '',
  imageSeed: '',
  imageUrl: '',
};

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

const Signaler = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // { reference } après création
  const [recent, setRecent] = useState([]);

  // Charge les derniers signalements publics
  useEffect(() => {
    let cancelled = false;
    signalementsApi.list({ limit: 8 })
      .then((data) => { if (!cancelled) setRecent(data.items); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [success]); // refetch après chaque création

  const stats = useMemo(() => {
    const c = { total: recent.length, resolved: 0, inProgress: 0, urgent: 0 };
    for (const s of recent) {
      if (s.status === 'resolu') c.resolved++;
      if (s.status === 'en_cours') c.inProgress++;
      if (s.priority === 'urgent') c.urgent++;
    }
    return c;
  }, [recent]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        priority: form.priority,
        location: form.location,
        anonymous: form.anonymous,
        reporterName: form.anonymous ? '' : form.reporterName,
        reporterEmail: form.anonymous ? '' : form.reporterEmail,
        reporterPhone: form.anonymous ? '' : form.reporterPhone,
        imageSeed: form.imageSeed,
        imageUrl: form.imageUrl,
      };
      const token = getToken(); // optionnel — si membre connecté
      const { signalement } = await signalementsApi.create(payload, token);
      setSuccess({ reference: signalement.reference });
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="sig-page">
      {/* ============== HERO ============== */}
      <header className="sig-hero">
        <div className="wrap">
          <span className="eyebrow">Signalement citoyen · Mairie de Cambérène</span>
          <h1>
            Signalez un problème<br />
            <span className="ital">dans votre village.</span>
          </h1>
          <p className="lede">
            Nid de poule, dépôt sauvage, lampadaire HS, fuite d'eau, problème de sécurité —
            décrivez-le en quelques mots, la mairie le prend en charge sous 5 jours.
          </p>
        </div>
      </header>

      {/* ============== SHELL ============== */}
      <div className="sig-shell">
        <div className="wrap" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 48 }}>
          {/* FORMULAIRE */}
          <section className="sig-form">
            <h2>Décrire le problème</h2>
            <p className="sub">Anonyme par défaut. Vous pouvez laisser un email pour suivre votre signalement.</p>

            {success && (
              <div className="sig-ok">
                ✓ Votre signalement a été enregistré sous la référence{' '}
                <b>{success.reference}</b>.<br />
                Vous pouvez le suivre publiquement depuis cette page.
              </div>
            )}

            {error && <div className="sig-err">{error}</div>}

            <form onSubmit={onSubmit}>
              <div className="field">
                <label>Titre court</label>
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ex : Nid de poule rue 8, lampadaire HS rue 22"
                />
              </div>

              <div className="field">
                <label>Description détaillée</label>
                <textarea
                  required
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Précisez le contexte, depuis quand, conséquences…"
                />
              </div>

              <div className="sig-row">
                <div className="field">
                  <label>Catégorie</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {SIG_CATEGORIES.filter((c) => c.id !== 'all').map((c) =>
                      <option key={c.id} value={c.id}>{c.lbl}</option>
                    )}
                  </select>
                </div>
                <div className="field">
                  <label>Priorité (subjective)</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                  >
                    <option value="faible">Faible — peut attendre</option>
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent — danger / handicap</option>
                  </select>
                </div>
              </div>

              <div className="field">
                <label>Localisation</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="Ex : Angle rue 8 / rue 12, devant la pharmacie"
                />
              </div>

              <div className="field">
                <label>Photo (URL ou seed Picsum · optionnel)</label>
                <input
                  type="text"
                  value={form.imageSeed || form.imageUrl}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^https?:\/\//.test(v)) setForm((f) => ({ ...f, imageUrl: v, imageSeed: '' }));
                    else setForm((f) => ({ ...f, imageSeed: v, imageUrl: '' }));
                  }}
                  placeholder="cb-sig-mon-probleme  ·  ou  https://…"
                />
              </div>

              <label className="sig-anon">
                <input
                  type="checkbox"
                  checked={form.anonymous}
                  onChange={(e) => setForm((f) => ({ ...f, anonymous: e.target.checked }))}
                />
                <span>
                  <b>Signalement anonyme</b><br />
                  Décochez si vous souhaitez laisser vos coordonnées (pour être recontacté par la mairie).
                </span>
              </label>

              {!form.anonymous && (
                <>
                  <div className="sig-row">
                    <div className="field">
                      <label>Votre nom</label>
                      <input
                        type="text"
                        value={form.reporterName}
                        onChange={(e) => setForm((f) => ({ ...f, reporterName: e.target.value }))}
                      />
                    </div>
                    <div className="field">
                      <label>Email</label>
                      <input
                        type="email"
                        value={form.reporterEmail}
                        onChange={(e) => setForm((f) => ({ ...f, reporterEmail: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label>Téléphone (optionnel)</label>
                    <input
                      type="tel"
                      value={form.reporterPhone}
                      onChange={(e) => setForm((f) => ({ ...f, reporterPhone: e.target.value }))}
                    />
                  </div>
                </>
              )}

              <button type="submit" className="sig-submit" disabled={saving}>
                {saving ? 'Envoi…' : 'Envoyer le signalement'} <span>→</span>
              </button>
            </form>
          </section>

          {/* SIDEBAR */}
          <aside className="sig-side">
            <div className="stats">
              <div className="cell"><b>{stats.total}</b><small>Récents</small></div>
              <div className="cell"><b>{stats.urgent}</b><small>Urgents</small></div>
              <div className="cell"><b>{stats.inProgress}</b><small>En cours</small></div>
              <div className="cell"><b>{stats.resolved}</b><small>Résolus</small></div>
            </div>

            <h3>Signalements récents</h3>
            <div className="recent">
              {recent.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--char-500)' }}>Aucun signalement public pour l'instant.</p>
              )}
              {recent.slice(0, 8).map((s) => (
                <div key={s.id} className="item">
                  <div>
                    <span className="ref">{s.reference} · {fmtDate(s.createdAt)}</span>
                    <span className="title">{s.title}</span>
                  </div>
                  <span className={`pill ${s.status}`}>{STATUS_LABEL[s.status] || s.status}</span>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default Signaler;
